"use client";
import React, { useEffect, useState } from "react";
import {
  ButtonComponent,
  DividerComponent,
  LoaderComponent,
  PaperComponent,
  SliderComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  useGetCustomerQuery,
  useApplyLoanMutation,
  useGetApplicationQuery,
  useCreateApplicationMutation,
  useProductDetailQuery,
  usePostUtmDetailsMutation,
} from "@/lib/redux";
import {
  CurrencyConverter,
  GET_PRODUCT_APPLICATION,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import {
  Stack,
  Typography,
  Grid,
  Box,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
} from "@mui/material";
import useLogoutHook from "@/lib/hooks/logoutHook";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import styles from "./applyloan.module.css";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import PieChart from "../PieChart/PieChart";
import PieChartComponent from "../PieChart/PieChart";
import EmiNoteBox from "../EmiNoteBox/EmiNoteBox";
import ContentHeader from "../ContentHeader/ContentHeader";

function EmiCalculator({
  amountRequired,
  tenureRequired,
  interestRequired,
  product,
}) {
  const searchParams = useSearchParams();
  // const params = {
  //   utmSource: searchParams.get("utmSource"),
  //   utmMedium: searchParams.get("utmMedium"),
  //   utmTerm: searchParams.get("utmTerm"),
  //   utmAdsetid: searchParams.get("utmAdsetid"),
  //   utmCampaign: searchParams.get("utmCampaign"),
  //   utmNetwork: searchParams.get("utmNetwork"),
  //   utmMatchtype: searchParams.get("utmMatchtype"),
  //   utmDevice: searchParams.get("utmDevice"),
  //   utmPlacement: searchParams.get("utmPlacement"),
  //   utmContent: searchParams.get("utmContent"),
  //   utmAdposition: searchParams.get("utmAdposition"),
  //   utmLocation: searchParams.get("utmLocation"),
  //   utmSitelink: searchParams.get("utmSitelink"),
  //   utmAudience: searchParams.get("utmAudience"),
  //   utmPromotion: searchParams.get("utmPromotion"),
  //   utmPrice: searchParams.get("utmPrice"),
  //   utmCampaignid: searchParams.get("utmCampaignid"),
  //   gadSource: searchParams.get("gadSource"),
  //   gclid: searchParams.get("gclid"),
  //   utmCampaigntype: searchParams.get("utmCampaigntype"),
  // };
  const router = useRouter();
  const t = useTranslations("ApplyLoan");
  const { handleLogout } = useLogoutHook();
  const [amount, setAmount] = useState(150000);
  const [tenure, setTenure] = useState(12);
  const [interestAmount, setInterestAmount] = useState(0);
  const [principalAmount, setPrincipalAmount] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    data: productData,
    isLoading: fetchingProductDetails,
    error: detailsFetchError,
  } = useProductDetailQuery({
    product: PRODUCT_HANDLER[product],
  });
  const {
    data: customerData,
    isLoading: fetchingCustomerData,
    isSuccess: customerDatafetched,
  } = useGetCustomerQuery("");
  const {
    data: applicationData,
    isLoading: fetchingApplications,
    error: applicationFetchError,
    isSuccess: applicationsFetched,
  } = useGetApplicationQuery("", {
    skip: !customerDatafetched,
  });
  const [
    createApplication,
    { error: applicationCreationError, isLoading: creatingApplication },
  ] = useCreateApplicationMutation();
  const [
    applyLoan,
    { data: applyLoanData, isLoading: applyingForLoan, error: applyingError },
  ] = useApplyLoanMutation();

  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const [postUtmParams, { data: utmResponse, error: utmError }] =
    usePostUtmDetailsMutation();

  const isLoading =
    fetchingApplications ||
    creatingApplication ||
    applyingForLoan ||
    fetchingProductDetails ||
    fetchingCustomerData;
  const isError =
    applicationFetchError ||
    applicationCreationError ||
    applyingError ||
    detailsFetchError;

  const [interestRate, setInterestRate] = useState(12);

  useEffect(() => {
    if (applicationsFetched && !productApplication?.appId) {
      createApplication({ product_type: PRODUCT_HANDLER[product] });
    }
  }, [applicationsFetched]);

  useEffect(() => {
    let principalAmount = (amount * interestRate * tenure) / 100;
    let interestAmount = principalAmount - amount;
    setInterestAmount(interestAmount);
    setPrincipalAmount(principalAmount);
  }, [amount, interestRate, tenure]);

  useEffect(() => {
    if (productApplication?.appId && customerData?.data) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.APPLY_LOAN_STARTED,
        ...productApplication,
        ...customerData?.data,
      });
    }
    if (productApplication?.requestedLoanAmount) {
      setAmount(productApplication?.requestedLoanAmount);
    }
    if (
      customerData?.data?.id &&
      productApplication?.appId &&
      params?.utmSource
    ) {
      postUtmParams({
        ...params,
        customerId: customerData?.data?.id,
        appId: productApplication.appId,
        utmInboundUrl: window?.location?.href,
      });
    }
  }, [customerData, productApplication]);

  useEffect(() => {
    if (applyLoanData?.statusCode === 202) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.TESTING_EVENT,
        ...productApplication,
        ...customerData?.data,
      });
      router.push(
        STAGE_BASED_ROUTING(productApplication?.stage) ||
          `/${product}/identity-details`,
      );
    }
  }, [applyLoanData, productApplication]);

  useEffect(() => {
    if (
      productApplication?.stage &&
      productApplication?.stage !== STAGE.PAN &&
      productApplication?.stage !== STAGE.CKYC &&
      productApplication?.stage !== STAGE.PERSONAL_DETAILS &&
      productApplication?.stage !== STAGE.ADD_ADDRESS
    ) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [productApplication]);

  const ApplyLoan = async () => {
    if (!productApplication?.appId) {
      await createApplication({ product_type: PRODUCT_HANDLER[product] });
    }
    applyLoan({
      appId: productApplication?.appId,
      requestedLoanAmount: amount.toString(),
    });
  };

  return (
    <Box sx={{ p: { xs: 0, md: 8 } }}>
      {/* <LoaderComponent open={isLoading} /> */}
      {!isMobile && (
        <ContentHeader
          heading={"Personal Loan EMI Calculator"}
          paragraph={
            "Wondering how to apply for a Personal Loan? Applying for a Personal Loan online has never been easier. By completing a quick and secure online application process, you can find an easy and accessible way to secure instant funds."
          }
        />
      )}
      <PaperComponent elevation={4} paperStyle={{ my: 1, p: 2, width: "100%" }}>
        <Grid container>
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={6}
            borderRight={2}
            borderColor={"#F0F0F0"}
          >
            {isMobile && (
              <Stack>
                <Typography textAlign={"center"}>
                  Personal Loan EMI Calculator
                </Typography>
              </Stack>
            )}
            <Stack>
              {amountRequired && (
                <>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems="center"
                    justifyContent="space-between"
                    mt={2}
                  >
                    <Typography
                      className={styles["amount-label"]}
                      sx={{ pl: 3 }}
                    >
                      {t("loanamount")}
                    </Typography>
                    <Typography
                      className={styles["amount-view"]}
                      sx={{ pr: 3 }}
                    >
                      {CurrencyConverter(amount)}
                    </Typography>
                  </Stack>
                  <Stack
                    sx={{ p: { xs: 1, sm: 3 } }}
                    className={styles["slider-container"]}
                  >
                    <SliderComponent
                      min={productData?.minLoanAmount}
                      max={productData?.maxLoanAmount}
                      value={amount}
                      step={10000}
                      amountRequired={amountRequired}
                      onChange={(_, newValue) => setAmount(newValue)}
                    />
                  </Stack>
                </>
              )}
              <DividerComponent sx={{ width: "90%", mx: "auto" }} />
              {interestRequired && (
                <>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems="center"
                    justifyContent="space-between"
                    mt={2}
                  >
                    <Typography
                      className={styles["amount-label"]}
                      sx={{ pl: 3 }}
                    >
                      Rate of Interest (P.a)
                    </Typography>
                    <Typography
                      className={styles["amount-view"]}
                      sx={{ pr: 3 }}
                    >
                      {interestRate}%
                    </Typography>
                  </Stack>
                  <Stack
                    sx={{ p: { xs: 1, sm: 3 } }}
                    className={styles["slider-container"]}
                  >
                    <SliderComponent
                      min={18}
                      max={30}
                      value={interestRate}
                      step={0.1}
                      interestRequired={interestRequired}
                      onChange={(_, newValue) => setInterestRate(newValue)}
                    />
                  </Stack>
                </>
              )}
              <DividerComponent sx={{ width: "90%", mx: "auto" }} />

              {tenureRequired && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems="center"
                  justifyContent="space-between"
                  mt={2}
                >
                  <Typography className={styles["amount-label"]} sx={{ pl: 3 }}>
                    {t("tenure")}
                  </Typography>
                  <Typography className={styles["amount-view"]} sx={{ pr: 3 }}>
                    {tenure} {t("months")}
                  </Typography>
                </Stack>
              )}
              <Stack
                sx={{ p: { xs: 1, sm: 3 } }}
                className={styles["slider-container"]}
              >
                <SliderComponent
                  minTenure={6}
                  maxTenure={36}
                  tenure={tenure}
                  tenureRequired={tenureRequired}
                  handleTenureChange={(_, newValue) => setTenure(newValue)}
                />
              </Stack>
            </Stack>
          </Grid>
          <Grid xs={12} sm={12} md={6} lg={6} sx={{ px: { xs: 1, md: 3 } }}>
            <Stack mt={2} spacing={2}>
              <PieChartComponent
                series={[
                  {
                    outerRadius: 80,
                    innerRadius: 50,
                    data: [
                      {
                        id: 0,
                        value: principalAmount,
                        label: "Principal",
                        color: WEB_COLORS.darkTeal,
                      },
                      {
                        id: 1,
                        value: interestAmount,
                        label: "Interest Amount",
                        color: "#00F200",
                      },
                    ],
                    cornerRadius: 10,
                    paddingAngle: 5,
                  },
                ]}
                width={300}
                height={300}
                slotProps={{
                  legend: {
                    direction: "row",
                    position: { vertical: "bottom", horizontal: "middle" },
                  },
                }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Typography className={styles["loan-details-label"]}>
                  EMI Amount
                </Typography>
                <Typography className={styles["loan-details-value"]}>
                  {CurrencyConverter(interestAmount / tenure)}/month
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Typography className={styles["loan-details-label"]}>
                  {t("interestrate")}
                </Typography>
                <Typography className={styles["loan-details-value"]}>
                  {CurrencyConverter(interestAmount)}
                  {/* {t("startingrate", { interest: productData?.interestRate })} */}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Typography className={styles["loan-details-label"]}>
                  {t("loanamount")}
                </Typography>
                <Typography className={styles["loan-details-value"]}>
                  {CurrencyConverter(amount)}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        <EmiNoteBox />
        <Stack
          gap={2}
          px={1}
          display={"flex"}
          justifyContent={"space-between"}
          direction={"row"}
        >
          <Stack>
            <Typography fontSize={14} color="#4D6767">
              Personal Loan Rates & Fees:
            </Typography>
            <Stack px={2}>
              <List
                sx={{
                  listStyleType: "disc",
                  color: "#7F9893",
                  fontSize: "12px",
                }}
              >
                <ListItem
                  disablePadding
                  sx={{ display: "list-item", fontSize: 14, py: 0.5 }}
                >
                  Loan Amount upto ₹ 7,00,000
                </ListItem>
                <ListItem
                  disablePadding
                  sx={{ display: "list-item", fontSize: 14, py: 0.5 }}
                >
                  Tenure: 12 to 36 months
                </ListItem>
                <ListItem
                  disablePadding
                  sx={{ display: "list-item", fontSize: 14, py: 0.5 }}
                >
                  Rate of Interest (ROI): Starting from 18% per year
                </ListItem>
                <ListItem
                  disablePadding
                  sx={{ display: "list-item", fontSize: 14, py: 0.5 }}
                >
                  Maximum APR: 45%
                </ListItem>
                <ListItem
                  disablePadding
                  sx={{ display: "list-item", fontSize: 14, py: 0.5 }}
                >
                  Processing Fee: 2.5% of loan amount + taxes as applicable
                </ListItem>
              </List>
            </Stack>
          </Stack>
          <Stack>
            <Grid item xs={12}>
              <Typography fontSize={14} color={"#4D6767"}>
                Example:
              </Typography>
            </Grid>
            <Grid
              container
              spacing={1}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                padding: "8px",
                marginTop: "8px",
              }}
            >
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Stack>
                  <Typography fontSize={8} color={"rgba(0, 65, 53, 0.48)"}>
                    Loan Amount
                  </Typography>
                  <Typography color={WEB_COLORS.primary} fontSize={12}>
                    ₹ 1,00,000
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Stack>
                  <Typography fontSize={8} color={"rgba(0, 65, 53, 0.48)"}>
                    Interest Rate
                  </Typography>
                  <Typography color={WEB_COLORS.primary} fontSize={12}>
                    18%
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Stack>
                  <Typography fontSize={8} color={"rgba(0, 65, 53, 0.48)"}>
                    Tenure
                  </Typography>
                  <Typography color={WEB_COLORS.primary} fontSize={12}>
                    12 Months
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Stack>
                  <Typography fontSize={8} color={"rgba(0, 65, 53, 0.48)"}>
                    Interest
                  </Typography>
                  <Typography color={WEB_COLORS.primary} fontSize={12}>
                    {CurrencyConverter(10588)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Stack>
                  <Typography fontSize={8} color={"rgba(0, 65, 53, 0.48)"}>
                    Processing Fee (incl. GST)
                  </Typography>
                  <Typography color={WEB_COLORS.primary} fontSize={12}>
                    {CurrencyConverter(2592)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Stack>
                  <Typography fontSize={8} color={"rgba(0, 65, 53, 0.48)"}>
                    Total Payable
                  </Typography>
                  <Typography color={WEB_COLORS.primary} fontSize={12}>
                    {CurrencyConverter(113538)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Stack>
                  <Typography fontSize={8} color={"rgba(0, 65, 53, 0.48)"}>
                    EMI
                  </Typography>
                  <Typography fontSize={12} color={WEB_COLORS.primary}>
                    {CurrencyConverter(9216)}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Stack>
        <Stack mt={5}>
          <ButtonComponent
            toolTipTitle={t("toolTipTitle")}
            type="submit"
            onClick={ApplyLoan}
          >
            {productApplication?.requestedLoanAmount
              ? t("updateloan")
              : t("applyloan")}
          </ButtonComponent>
        </Stack>
      </PaperComponent>
    </Box>
  );
}

export default EmiCalculator;
